import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Pressable,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import { format } from "date-fns";
import { useNavigation, useFocusEffect } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type EnumItem = {
    enumerationId: string;
    value: string;
    parameter?: string | number | null;
};

type TotalWorshipsDto = {
    totalMinutes: number;
    totalMarks: number;
    minutesOfWorships: any;
    marksOfWorships: number;
    readingContent?: string | null;
    existingEnumValue?: number | null;
    isEditable: boolean;
};

const MIN_REQUIRED_MINUTES = 30;
const MARKS_PER_BLOCK = 6;
const MINUTES_PER_BLOCK = 30;

/* ---------- TIME HELPERS ---------- */

const timeOnlyToMinutes = (time: any) => {
    if (!time) return 0;

    if (typeof time === "number") return time;

    if (typeof time === "string") {
        const parts = time.split(":");
        const h = parseInt(parts[0] || "0", 10);
        const m = parseInt(parts[1] || "0", 10);
        return h * 60 + m;
    }

    return 0;
};

const minutesToTimeOnly = (mins: number) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;

    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");

    return `${hh}:${mm}:00`;
};

/* ---------- MARKS LOGIC ---------- */

const calculateHearingMarks = (mins: number) => {
    if (mins < MIN_REQUIRED_MINUTES) return 0;

    const blocks = Math.floor(mins / MINUTES_PER_BLOCK);
    return blocks * MARKS_PER_BLOCK;
};

export function HearingScreen() {
    const navigation = useNavigation<any>();
    const { user } = useAuth();

    const todayStr = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
    const [date, setDate] = useState(todayStr);

    const [loading, setLoading] = useState(false);

    const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);

    const [minutes, setMinutes] = useState<string>("0");
    const [marks, setMarks] = useState<number>(0);

    const [totalMinutes, setTotalMinutes] = useState<number>(0);
    const [totalMarks, setTotalMarks] = useState<number>(0);

    const [worshipId, setWorshipId] = useState<string | null>(null);
    const [hearingContent, setHearingContent] = useState("");
    const [isEditable, setIsEditable] = useState(true);

    const minutesValidationError = useMemo(
        () => getMinutesValidationError(minutes, MIN_REQUIRED_MINUTES),
        [minutes]
    );
    const parsedMinutes = useMemo(() => parseValidMinutes(minutes) ?? 0, [minutes]);
    const isMinutesValid = !minutesValidationError;

    const minutesPerMarkText = useMemo(
        () => "30 Minutes = 6 Marks",
        []
    );

    const marksRuleText = useMemo(
        () => "30-59 min = 6, 60-89 min = 12, 90-119 min = 18",
        []
    );

    useEffect(() => {
        setMarks(calculateHearingMarks(parsedMinutes));
    }, [parsedMinutes]);

    const openDatePicker = () => {
        DateTimePickerAndroid.open({
            value: new Date(date),
            mode: "date",
            maximumDate: new Date(),
            onChange: (event, selectedDate) => {
                if (event.type === "set" && selectedDate) {
                    setDate(format(selectedDate, "yyyy-MM-dd"));
                }
            },
        });
    };

    const loadEnumerationAndSetHearing = useCallback(async () => {
        try {
            setLoading(true);

            const res = await TimeBasedService.getEnumeration("WorshipType");
            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load worship types");
                return;
            }

            const list = (res?.data || []) as EnumItem[];
            const hearing = list.find(
                (x) => (x.value || "").trim().toLowerCase() === "hearing"
            );

            if (!hearing?.enumerationId) {
                Flash.error("Hearing worship type not found in Enumeration");
                return;
            }

            setWorshipTypeId(hearing.enumerationId);
        } catch (e: any) {
            Flash.error(e?.message || "Enumeration load error");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAgainstDate = useCallback(
        async (dateStr: string) => {
            if (!user?.userId) return;
            if (!worshipTypeId) return;

            try {
                setLoading(true);

                const res = await TimeBasedService.getWorshipAgainstDate({
                    date: dateStr,
                    worshipType: worshipTypeId,
                });

                if (res?.status !== 200) {
                    Flash.error(res?.message || "Failed to load");
                    return;
                }

                const data = (res?.data || null) as TotalWorshipsDto | null;

                setHearingContent(data?.readingContent ?? "");
                setTotalMinutes(data?.totalMinutes ?? 0);
                setTotalMarks(Number(data?.totalMarks ?? 0));
                setIsEditable(data?.isEditable ?? true);

                const mins = timeOnlyToMinutes(data?.minutesOfWorships);
                setMinutes(String(mins));
                setMarks(calculateHearingMarks(mins));

                setWorshipId(null);
            } catch (e: any) {
                Flash.error(e?.message || "Something went wrong");
            } finally {
                setLoading(false);
            }
        },
        [user?.userId, worshipTypeId]
    );

    useEffect(() => {
        loadEnumerationAndSetHearing();
    }, [loadEnumerationAndSetHearing]);

    useEffect(() => {
        loadAgainstDate(date);
    }, [date, loadAgainstDate]);

    useFocusEffect(
        useCallback(() => {
            loadAgainstDate(date);
        }, [date, loadAgainstDate])
    );

    const onSave = async () => {
        if (!user?.userId) {
            Flash.error("User not found");
            return;
        }

        if (!worshipTypeId) {
            Flash.error("Worship type not ready");
            return;
        }

        if (minutesValidationError) {
            Flash.error(minutesValidationError);
            return;
        }

        const calculatedMarks = calculateHearingMarks(parsedMinutes);

        try {
            setLoading(true);

            const payload = {
                worshipId: worshipId ?? null,
                userId: user.userId,
                date,
                worshipType: worshipTypeId,
                minutes: minutesToTimeOnly(parsedMinutes),
                marks: calculatedMarks,
                readingContent: hearingContent?.trim() || null,
            };

            const res = await TimeBasedService.create(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to save");
                return;
            }

            Flash.success("Success!", "Hearing Saved Successfully!");
            navigation.navigate("HearingHistory");
        } catch (e: any) {
            Flash.error(e?.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DivineBackground overlayOpacity={0.9}>
            <View style={{ flex: 1 }}>
                <Header title="Hearing" showBack />

                <ScrollView
                    contentContainerStyle={styles.container}
                    showsVerticalScrollIndicator={false}
                >
                    <View style={styles.summaryCard}>
                        <Text style={styles.summaryTitle}>This Month's Offering</Text>

                        <View style={styles.summarySplit}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.summaryMini}>TOTAL MINUTES</Text>
                                <Text style={styles.summaryValue}>{totalMinutes}</Text>
                            </View>

                            <View style={{ flex: 1, alignItems: "center" }}>
                                <View style={styles.divider} />
                            </View>

                            <View style={{ flex: 1, alignItems: "flex-start" }}>
                                <Text style={styles.summaryMini}>TOTAL MARKS</Text>
                                <Text style={styles.summaryValue}>{totalMarks}</Text>
                            </View>
                        </View>
                    </View>

                    <View style={styles.card}>
                        <View style={styles.topRow}>
                            <Pressable onPress={openDatePicker} style={styles.dateChip}>
                                <Text style={styles.dateChipIcon}>📅</Text>
                                <Text style={styles.dateChipText}>
                                    {format(new Date(date), "dd-MM-yyyy")}
                                </Text>
                            </Pressable>

                            <Pressable
                                onPress={() => navigation.navigate("HearingHistory")}
                                style={styles.historyBtn}
                            >
                                <Text style={styles.historyIcon}>🕘</Text>
                                <Text style={styles.historyText}>History</Text>
                            </Pressable>
                        </View>

                        <View style={{ marginTop: 12 }}>
                            <Input
                                label="Number of Minutes"
                                keyboardType="number-pad"
                                value={minutes}
                                onChangeText={(t) => setMinutes(t.replace(/[^\d]/g, ""))}
                            />

                            {minutesValidationError ? (
                                <Text style={styles.validationText}>
                                    {minutesValidationError}
                                </Text>
                            ) : null}

                            <Input
                                label="Hearing Content"
                                value={hearingContent}
                                onChangeText={(text) => {
                                    if (text.length <= 1000) setHearingContent(text);
                                }}
                                placeholder="e.g. Bhagavatam lecture..."
                                multiline
                                numberOfLines={4}
                                textAlignVertical="top"
                                maxLength={1000}
                                inputStyle={{ height: 100 }}
                            />

                            <Text style={styles.charCount}>
                                {hearingContent.length}/1000
                            </Text>

                            <Text style={styles.hintText}>{minutesPerMarkText}</Text>
                            {/* <Text style={styles.ruleText}>{marksRuleText}</Text> */}

                            <View style={styles.calcRow}>
                                <Text style={styles.calcLabel}>Calculated Marks:</Text>
                                <Text style={styles.calcValue}>{marks}</Text>
                            </View>
                        </View>

                        {isEditable && (
                            <View style={styles.btnRow}>
                                <Pressable
                                    onPress={() => navigation.goBack()}
                                    style={({ pressed }) => [
                                        styles.cancelBtn,
                                        pressed ? { opacity: 0.95 } : null,
                                    ]}
                                    disabled={loading}
                                >
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>

                                <View style={{ flex: 1 }}>
                                    <Button
                                        title={loading ? "Saving..." : "Save"}
                                        onPress={onSave}
                                        disabled={loading || !isMinutesValid}
                                    />
                                </View>
                            </View>
                        )}

                        {loading ? (
                            <View style={{ marginTop: 12, alignItems: "center" }}>
                                <ActivityIndicator />
                            </View>
                        ) : null}
                    </View>
                </ScrollView>
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, paddingBottom: 30, gap: 12 },

    summaryCard: {
        borderRadius: 14,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.82)",
        borderWidth: 1,
        borderColor: "rgba(37,99,235,0.15)",
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 12,
        shadowOffset: { width: 0, height: 8 },
        elevation: 3,
    },
    summaryTitle: { fontSize: 13, fontWeight: "900", color: "rgba(17,24,39,0.85)" },

    summarySplit: { flexDirection: "row", alignItems: "center", marginTop: 10 },
    summaryMini: { fontSize: 10, fontWeight: "900", color: "rgba(0,0,0,0.35)" },
    summaryValue: { marginTop: 4, fontSize: 20, fontWeight: "900", color: "#1D4ED8" },

    divider: { width: 1, height: 40, backgroundColor: "rgba(0,0,0,0.06)" },

    card: {
        borderRadius: 16,
        padding: 14,
        backgroundColor: "rgba(255,255,255,0.88)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },

    topRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },

    dateChip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        backgroundColor: "rgba(238,242,255,0.85)",
        borderWidth: 1,
        borderColor: "rgba(99,102,241,0.25)",
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 999,
    },
    dateChipIcon: { fontSize: 14 },
    dateChipText: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.85)" },

    historyBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingHorizontal: 10,
        height: 34,
        borderRadius: 10,
        backgroundColor: "rgba(255,255,255,0.55)",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },
    historyIcon: { fontSize: 14 },
    historyText: { fontSize: 12, fontWeight: "800", color: "rgba(17,24,39,0.70)" },

    validationText: {
        marginTop: 6,
        fontSize: 11,
        fontWeight: "800",
        color: "#DC2626",
    },

    charCount: {
        fontSize: 11,
        color: "rgba(0,0,0,0.45)",
        textAlign: "right",
        marginTop: 4,
    },

    hintText: {
        marginTop: 6,
        fontSize: 11,
        fontWeight: "800",
        color: "rgba(17,24,39,0.55)",
    },

    ruleText: {
        marginTop: 4,
        fontSize: 11,
        fontWeight: "700",
        color: "rgba(17,24,39,0.45)",
    },

    calcRow: {
        marginTop: 10,
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 10,
        backgroundColor: "rgba(238,242,255,0.65)",
        borderWidth: 1,
        borderColor: "rgba(99,102,241,0.18)",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },
    calcLabel: { fontSize: 13, fontWeight: "800", color: "rgba(17,24,39,0.65)" },
    calcValue: { fontSize: 20, fontWeight: "900", color: "#4F46E5" },

    btnRow: { flexDirection: "row", gap: 10, marginTop: 14 },
    cancelBtn: {
        flex: 1,
        height: 46,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.12)",
        backgroundColor: "rgba(255,255,255,0.9)",
        alignItems: "center",
        justifyContent: "center",
    },
    cancelText: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.60)" },
});
